import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {Box} from "@mui/material";
import Typography from "@mui/material/Typography";
import {visuallyHidden} from "@mui/utils";
import type {TFunction} from "i18next";
import React, {forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState} from "react";
import {
    SignatureAction,
    SignatureBaseline,
    SignatureBox,
    SignatureFoot,
    SignatureLock,
    SignatureNotice,
    SignaturePlaceholder
} from "./SigningSignatureGuides";
import {drawStrokes, exportPng, hasEnoughInk, PadSize, pointIn, redrawPad, SignatureStroke} from "./signaturePad.util";

export interface SignatureInk {
    hasStrokes: boolean;
    enough: boolean;
}

export interface SigningSignaturePadHandle {
    toDataUrl: () => string | undefined;
    clear: () => void;
}

interface Props {
    t: TFunction;
    disabled?: boolean;
    disabledMessage?: string;
    notice?: string;
    onInkChange: (ink: SignatureInk) => void;
    /**
     * The end of the signing block, the send button. Until the pad and it are on screen, a finger on
     * the pad scrolls the page instead of drawing: a guest scrolling past the pad must not sign.
     */
    endRef?: React.RefObject<HTMLElement>;
}

const pixelRatio = () => Math.min(window.devicePixelRatio || 1, 3);
const HINT_DELAY_MS = 700;
/** How far a finger may move and still tap. */
const TAP_SLOP_PX = 10;

/**
 * Whether the pad and [end] are both on screen; a block taller than the screen can't be, so then the
 * pad alone. Always true without IntersectionObserver.
 */
const useOnScreen = (pad: React.RefObject<HTMLElement>, end?: React.RefObject<HTMLElement>): boolean => {
    const [onScreen, setOnScreen] = useState(true);

    useEffect(() => {
        const padElement = pad.current;
        const endElement = end?.current;
        if (!padElement || typeof IntersectionObserver === 'undefined') return;

        const whole = new Map<Element, boolean>();
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => whole.set(entry.target, entry.intersectionRatio > 0.98));
            const tooTall = !!endElement
                && endElement.getBoundingClientRect().bottom - padElement.getBoundingClientRect().top > window.innerHeight;
            setOnScreen(!!whole.get(padElement) && (!endElement || !!whole.get(endElement) || tooTall));
        }, {threshold: [0, 0.5, 0.98, 1]});
        observer.observe(padElement);
        if (endElement) observer.observe(endElement);
        return () => observer.disconnect();
    }, [pad, end]);

    return onScreen;
};

const SigningSignaturePad = forwardRef<SigningSignaturePadHandle, Props>(
    ({t, disabled, disabledMessage, notice, onInkChange, endRef}, ref) => {
        const headingId = useId();
        const areaRef = useRef<HTMLDivElement>(null);
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const strokesRef = useRef<SignatureStroke[]>([]);
        const sizeRef = useRef<PadSize>({width: 0, height: 0});
        const pointerRef = useRef<number | null>(null);
        // A finger that only passes over the pad: the page scrolls, and a tap brings the block on screen
        const passingRef = useRef<{pointerId: number, x: number, y: number} | null>(null);
        const drawable = useOnScreen(areaRef, endRef);
        const [ink, setInk] = useState<SignatureInk>({hasStrokes: false, enough: false});
        const [idle, setIdle] = useState(true);
        const idleTimerRef = useRef<number | undefined>(undefined);
        useEffect(() => () => window.clearTimeout(idleTimerRef.current), []);

        const onInkChangeRef = useRef(onInkChange);
        onInkChangeRef.current = onInkChange;
        useEffect(() => {
            onInkChangeRef.current(ink);
        }, [ink]);

        const measure = useCallback(() => {
            const hasStrokes = strokesRef.current.length > 0;
            const enough = hasEnoughInk(strokesRef.current, sizeRef.current);
            setInk(current => (
                current.hasStrokes === hasStrokes && current.enough === enough ? current : {hasStrokes, enough}
            ));
        }, []);

        const redraw = useCallback(() => {
            const context = canvasRef.current?.getContext('2d');
            if (context) redrawPad(context, strokesRef.current, sizeRef.current, pixelRatio());
        }, []);

        useEffect(() => {
            const area = areaRef.current;
            const canvas = canvasRef.current;
            if (!area || !canvas) return;

            const resize = () => {
                const {width, height} = area.getBoundingClientRect();
                if (!width || !height) return;

                sizeRef.current = {width, height};
                canvas.width = Math.round(width * pixelRatio());
                canvas.height = Math.round(height * pixelRatio());
                redraw();
                measure();
            };

            if (typeof ResizeObserver === 'undefined') {
                resize();
                window.addEventListener('resize', resize);
                return () => window.removeEventListener('resize', resize);
            }

            const observer = new ResizeObserver(resize);
            observer.observe(area);
            return () => observer.disconnect();
        }, [measure, redraw]);

        const clearRef = useRef<() => void>(() => undefined);
        useImperativeHandle(ref, () => ({
            toDataUrl: () => exportPng(strokesRef.current, sizeRef.current),
            clear: () => clearRef.current(),
        }), []);

        const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
            const active = document.activeElement;
            if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) active.blur();
            if (disabled || (pointerRef.current !== null && !event.isPrimary)) return;
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            if (event.pointerType === 'touch' && !drawable) {
                passingRef.current = {pointerId: event.pointerId, x: event.clientX, y: event.clientY};
                return;
            }

            event.preventDefault();
            try {
                event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
            }
            pointerRef.current = event.pointerId;
            window.clearTimeout(idleTimerRef.current);
            setIdle(false);

            const stroke = [pointIn(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect())];
            strokesRef.current = [...strokesRef.current, stroke];
            const context = canvasRef.current?.getContext('2d');
            if (context) drawStrokes(context, [stroke], sizeRef.current);
            measure();
        };

        const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
            if (event.pointerId !== pointerRef.current) return;
            event.preventDefault();
            const stroke = strokesRef.current[strokesRef.current.length - 1];
            if (!stroke) return;

            const native = event.nativeEvent;
            const samples = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
            const rect = event.currentTarget.getBoundingClientRect();
            const points = (samples.length ? samples : [native]).map(sample => pointIn(sample.clientX, sample.clientY, rect));

            const segment = [stroke[stroke.length - 1], ...points];
            stroke.push(...points);
            const context = canvasRef.current?.getContext('2d');
            if (context) drawStrokes(context, [segment], sizeRef.current);
        };

        const endStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
            if (event.pointerId !== pointerRef.current) return;
            pointerRef.current = null;
            measure();
            window.clearTimeout(idleTimerRef.current);
            idleTimerRef.current = window.setTimeout(() => setIdle(true), HINT_DELAY_MS);
        };

        const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
            const passing = passingRef.current;
            passingRef.current = null;
            if (passing?.pointerId === event.pointerId && Math.hypot(event.clientX - passing.x, event.clientY - passing.y) < TAP_SLOP_PX) {
                const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
                endRef?.current?.scrollIntoView({block: 'end', behavior: reduce ? 'auto' : 'smooth'});
            }
            endStroke(event);
        };

        const onPointerCancel = (event: React.PointerEvent<HTMLCanvasElement>) => {
            passingRef.current = null;
            endStroke(event);
        };

        const clear = () => {
            strokesRef.current = [];
            pointerRef.current = null;
            redraw();
            measure();
        };
        clearRef.current = clear;

        const tooSmall = idle && ink.hasStrokes && !ink.enough;

        return (
            <Box role={'group'} aria-labelledby={headingId}>
                <Typography id={headingId} component={'h2'} sx={visuallyHidden}>
                    {t('Signature')}
                </Typography>

                <SignatureBox touchLock={drawable}>
                    <SignatureBaseline/>
                    <SignaturePlaceholder text={t('Sign here')} hidden={ink.hasStrokes || (!!disabled && !!disabledMessage)}/>

                    <Box ref={areaRef} sx={{position: 'absolute', inset: 0}}>
                        <canvas
                            ref={canvasRef}
                            aria-label={t('Signature field')}
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            onPointerCancel={onPointerCancel}
                            onLostPointerCapture={endStroke}
                            style={{
                                display: 'block',
                                width: '100%',
                                height: '100%',
                                touchAction: drawable ? 'none' : 'manipulation',
                                cursor: disabled ? 'default' : 'crosshair',
                            }}
                        />
                    </Box>

                    <SignatureFoot
                        label={t('Your signature')}
                        action={(
                            <SignatureAction
                                icon={<RefreshRoundedIcon aria-hidden sx={{fontSize: 18}}/>}
                                hidden={!ink.hasStrokes}
                                disabled={disabled}
                                onClick={clear}
                            >
                                {t('Redraw')}
                            </SignatureAction>
                        )}
                    />
                    <SignatureNotice
                        message={notice ?? (tooSmall ? t('Please sign a little larger.') : undefined)}
                        attention={!!notice}
                    />
                    {disabled && !!disabledMessage && <SignatureLock message={disabledMessage}/>}
                </SignatureBox>
            </Box>
        );
    }
);

SigningSignaturePad.displayName = 'SigningSignaturePad';

export default SigningSignaturePad;
